import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Permission } from '@kreart/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/useAuth';
import {
  drawerSystemsApi,
  hardwareApi,
  materialsApi,
} from '@/features/catalog/components/admin/api';

/** The catalog tables that carry a default markup. */
export type CoefficientCatalogKind = 'material' | 'hardware' | 'drawerSystem';

/** The catalog row a coefficient field is typing against. */
export interface CoefficientTarget {
  kind: CoefficientCatalogKind;
  /** Material / Hardware / DrawerSystemVariant id. */
  id: string;
  /** The variant's parent system — the write is nested under it. Variants only. */
  systemId?: string | null;
  /** What the user sees: the item's name, prefixed by where it is being used. */
  label: string;
  /** What the catalog says today. */
  catalogCoefficient: string;
}

interface PendingAsk extends CoefficientTarget {
  /** The markup just typed. */
  coefficient: string;
}

const QUERY_KEY_BY_KIND: Record<CoefficientCatalogKind, string> = {
  material: 'catalog-materials',
  hardware: 'catalog-hardware',
  drawerSystem: 'catalog-drawer-system-variants',
};

/**
 * Asks, the moment a coefficient field is left, whether the number just typed
 * should become the catalog's default for that board / fitting / slide system.
 *
 * Only ever an offer, and only ever about the future. The item being edited
 * keeps the number either way — every use snapshots its own markup, which is
 * the whole point of storing it per use — so declining changes nothing, and
 * accepting changes nothing about what was quoted before.
 *
 * Asked at blur rather than at save so the question arrives while the user is
 * still looking at the field that provoked it: "2.5 → 3.2 on PAL Alb" means
 * something with the cursor still in that box, and very little half a form
 * later. Each (row, value) is asked at most once, so tabbing back through a
 * field that was already answered stays quiet.
 */
export function useCatalogCoefficientPrompt(): {
  /**
   * Call from a coefficient field's `onBlur`. A no-op when there is nothing to
   * ask: an unparseable or empty value, a value that matches the catalog, a
   * user who cannot write the catalog, or a question already asked.
   */
  ask: (target: CoefficientTarget, coefficient: string) => void;
  dialog: ReactNode;
} {
  const { t } = useTranslation();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingAsk | null>(null);
  // Every (row, value) already put to the user, so re-entering a field the user
  // has answered — or a blur fired by opening the dialog itself — does not ask
  // again. Kept across the whole dialog session, which is the span over which
  // re-asking would read as nagging.
  const answered = useRef<Set<string>>(new Set());

  // Writing the catalog needs CATALOG_UPDATE, which designers do not have even
  // though they build cabinets all day. Asking them would end in a 403 after
  // they had already said yes.
  const mayWriteCatalog = auth.user?.permissions.includes(Permission.CATALOG_UPDATE) ?? false;

  const save = useMutation({
    mutationFn: async (target: PendingAsk) => {
      const body = { coefficient: target.coefficient };
      if (target.kind === 'material') return materialsApi.update(target.id, body);
      if (target.kind === 'hardware') return hardwareApi.update(target.id, body);
      if (!target.systemId) {
        throw new Error('A drawer system variant needs its parent system to be updated.');
      }
      return drawerSystemsApi.updateVariant(target.systemId, target.id, body);
    },
    onSuccess: (_result, target) => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BY_KIND[target.kind]] });
    },
  });

  const ask = useCallback(
    (target: CoefficientTarget, coefficient: string) => {
      if (!mayWriteCatalog) return;
      // One question at a time. A second field blurring while the dialog is up
      // would otherwise replace the one on screen with a different item's
      // numbers under the user's cursor.
      if (pending) return;
      const typed = Number(coefficient);
      const catalog = Number(target.catalogCoefficient);
      if (!Number.isFinite(typed) || typed <= 0) return;
      if (!Number.isFinite(catalog)) return;
      if (typed === catalog) return;
      const key = `${target.kind}:${target.id}:${typed}`;
      if (answered.current.has(key)) return;
      answered.current.add(key);
      setPending({ ...target, coefficient });
    },
    [mayWriteCatalog, pending],
  );

  const close = () => setPending(null);

  const dialog = (
    <Dialog open={pending !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('pricing.coefficientPrompt.title')}</DialogTitle>
          <DialogDescription>{t('pricing.coefficientPrompt.description')}</DialogDescription>
        </DialogHeader>
        {pending && (
          <p className="text-sm">
            {pending.label}
            <span className="ml-2 text-muted-foreground tabular-nums">
              {pending.catalogCoefficient} → {pending.coefficient}
            </span>
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={save.isPending}>
            {t('pricing.coefficientPrompt.onlyThisItem')}
          </Button>
          <Button
            type="button"
            disabled={save.isPending}
            onClick={async () => {
              if (!pending) return;
              try {
                await save.mutateAsync(pending);
              } finally {
                // The item keeps the typed number regardless, so a failed write
                // is not worth trapping the user behind: the dialog closes and
                // the catalog simply stays as it was.
                close();
              }
            }}
          >
            {save.isPending ? t('common.loading') : t('pricing.coefficientPrompt.saveToCatalog')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { ask, dialog };
}

/**
 * The catalog row behind a coefficient field, resolved from the id the form
 * holds.
 *
 * By id rather than from the picker's `onSelectedOption`, because that fires
 * only when a human picks: a cabinet opened for editing has its materials and
 * feronerie already filled in and never reports them, and the offer would be
 * silently dead on every edit — which is most of the time a markup gets retyped.
 *
 * Returns null while there is nothing to ask about: no id, no permission to
 * write the catalog, or the read still in flight.
 */
export function useCoefficientTarget(
  kind: CoefficientCatalogKind,
  id: string | null | undefined,
  label: string,
  options?: { enabled?: boolean },
): CoefficientTarget | null {
  const auth = useAuth();
  const mayWriteCatalog = auth.user?.permissions.includes(Permission.CATALOG_UPDATE) ?? false;
  const enabled = Boolean(id) && mayWriteCatalog && options?.enabled !== false;

  const query = useQuery({
    queryKey: [QUERY_KEY_BY_KIND[kind], 'coefficient', id],
    queryFn: async () => {
      if (kind === 'material') {
        const item = await materialsApi.get(id!);
        return { coefficient: item.coefficient, name: item.name, systemId: null };
      }
      if (kind === 'hardware') {
        const item = await hardwareApi.get(id!);
        return { coefficient: item.coefficient, name: item.name, systemId: null };
      }
      const item = await drawerSystemsApi.getVariantById(id!);
      return {
        coefficient: item.coefficient,
        name: item.offerName ?? item.description,
        systemId: item.systemId,
      };
    },
    enabled,
    // The catalog default moves rarely and a stale read only costs a question
    // that should not have been asked; a fresh one costs a request per field.
    staleTime: 60_000,
    meta: { silent: true },
  });

  if (!enabled || !id || !query.data) return null;
  return {
    kind,
    id,
    systemId: query.data.systemId,
    label: `${label} — ${query.data.name}`,
    catalogCoefficient: query.data.coefficient,
  };
}
